import {
  Box,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useCallback, useMemo, useRef, useState } from "react";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";

import {
  isTaskTypeForMangrove,
  isTaskTypeForProtea,
  isTaskTypeRiskConcern,
  isTaskTypeSelfTestConcern,
  isTaskTypeToFollowup,
  NoteEntry,
  TaskType,
} from "../features/data/patientApiSlice";

import { createStyleSheet } from "../utils/styles";
import { useTranslation } from "react-i18next";
import Notes, { NotesRef } from "./Notes";
import IconButton from "./IconButton";

type Props = {
  taskType: TaskType;
  onAddNote: (content: string) => void;
  onComplete: (completeData: {
    actionsTaken: string[];
    notes: string;
  }) => Promise<void> | void;
  notes: NoteEntry[];
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MENU_PROPS = {
  slotProps: {
    paper: {
      style: {
        maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP,
      },
    },
  },
};

enum ActionType {
  chat_with_client = "chat_with_client",
  nudge_sent = "nudge_sent",
  scheduled_bus_appt = "scheduled_bus_appt",
  referred_to_social_worker = "referred_to_social_worker",
  referred_for_services = "referred_for_services",
  handled_by_chatbot = "handled_by_chatbot",
  incorrect_flag = "incorrect_flag",
  other = "other",
}

const getActionsToTakeOptions = (taskType: TaskType) => {
  if (
    taskType === TaskType.request_to_chat ||
    taskType === TaskType.urgent_request_to_chat
  ) {
    return [
      ActionType.chat_with_client,
      ActionType.nudge_sent,
      ActionType.handled_by_chatbot,
      ActionType.incorrect_flag,
      ActionType.other,
    ];
  } else if (taskType === TaskType.followup) {
    return [
      ActionType.chat_with_client,
      ActionType.nudge_sent,
      ActionType.scheduled_bus_appt,
      ActionType.handled_by_chatbot,
      ActionType.incorrect_flag,
      ActionType.other,
    ];
  } else if (isTaskTypeSelfTestConcern(taskType)) {
    return [
      ActionType.chat_with_client,
      ActionType.nudge_sent,
      ActionType.scheduled_bus_appt,
      ActionType.handled_by_chatbot,
      ActionType.incorrect_flag,
      ActionType.other,
    ];
  } else if (taskType === TaskType.concern_chat_content) {
    return [
      ActionType.chat_with_client,
      ActionType.nudge_sent,
      ActionType.scheduled_bus_appt,
      ActionType.referred_to_social_worker,
      ActionType.handled_by_chatbot,
      ActionType.incorrect_flag,
      ActionType.other,
    ];
  } else if (isTaskTypeRiskConcern(taskType)) {
    return [
      ActionType.chat_with_client,
      ActionType.nudge_sent,
      ActionType.scheduled_bus_appt,
      ActionType.referred_to_social_worker,
      ActionType.handled_by_chatbot,
      ActionType.incorrect_flag,
      ActionType.other,
    ];
  } else if (isTaskTypeForMangrove(taskType) || isTaskTypeForProtea(taskType)) {
    return [
      ActionType.chat_with_client,
      ActionType.nudge_sent,
      ActionType.referred_for_services,
      ActionType.handled_by_chatbot,
      ActionType.incorrect_flag,
      ActionType.other,
    ];
  } else if (
    isTaskTypeToFollowup(taskType) ||
    taskType === TaskType.concern_self_reported_hiv_positive_DEPRECATED
  ) {
    return [
      ActionType.chat_with_client,
      ActionType.nudge_sent,
      ActionType.scheduled_bus_appt,
      ActionType.handled_by_chatbot,
      ActionType.incorrect_flag,
      ActionType.other,
    ];
  }
  return [];
};

const InlineTaskCompletion = ({ taskType, onAddNote, onComplete, notes }: Props) => {
  const { t } = useTranslation("CompleteTaskDialog");

  const [actionTaken, setActionTaken] = useState<string>("");
  const [noteOpen, setNoteOpen] = useState(false);

  const actionsToTake = useMemo(
    () => getActionsToTakeOptions(taskType),
    [taskType]
  );

  const notesRef = useRef<NotesRef | null>(null);

  const handleSelectionChange = useCallback(
    (event: SelectChangeEvent<typeof actionTaken>) => {
      const {
        target: { value },
      } = event;
      setActionTaken(value);
    },
    []
  );

  const handleCompleteTask = useCallback(async () => {
    const notesValueCandidate = notesRef.current?.getNoteValue();
    onComplete({
      actionsTaken: [actionTaken],
      notes: notesValueCandidate || "",
    });
  }, [actionTaken, onComplete]);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.actionRow}>
        <FormControl size="small" sx={styles.actionTakenInput}>
          <InputLabel id="action-taken-select-label">
            {t("actionsTaken")}
          </InputLabel>

          <Select
            labelId="action-taken-select-label"
            id="action-taken-select"
            value={actionTaken}
            onChange={handleSelectionChange}
            input={<OutlinedInput label={t("actionsTaken")} />}
            renderValue={(selected) => t("common:actionsTaken:" + selected)}
            MenuProps={MENU_PROPS}
          >
            {actionsToTake.map((action) => (
              <MenuItem key={action} value={action}>
                <Radio checked={actionTaken === action} />
                <ListItemText primary={t("common:actionsTaken:" + action)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Note Toggle Button */}
        <IconButton
          onClick={() => setNoteOpen((prev) => !prev)}
          sx={[
            styles.noteButton as object,
            noteOpen ? (styles.noteButtonActive as object) : {},
          ]}
          tooltip={t("completionNotes")}
          icon={<EditOutlinedIcon />}
        />

        {/* Complete Button */}
        <IconButton
          disabled={!actionTaken.length}
          onClick={handleCompleteTask}
          sx={[
            styles.completeButton as object,
            actionTaken.length > 0
              ? (styles.completeButtonActive as object)
              : {},
          ]}
          tooltip={t("completeTaskTitle")}
          icon={<CheckIcon />}
        />
      </Box>

      {/* Notes */}
      <Notes ref={notesRef} notes={notes} onAddNote={noteOpen ? onAddNote : undefined} />
    </Box>
  );
};

const styles = createStyleSheet({
  container: {
    marginTop: 1,
    width: "100%",
  },
  actionRow: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    width: "100%",
  },
  noteButton: {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
  },
  noteButtonActive: {
    borderColor: "primary.main",
    color: "primary.main",
  },
  completeButton: {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
    color: "grey.400",
  },
  completeButtonActive: {
    borderColor: "primary.main",
    color: "primary.main",
  },
  actionTakenInput: {
    width: "40%",
    background: (theme) => theme.palette.common.white,
  },
});

export default InlineTaskCompletion;
